import type { CropperCanvas, CropperImage } from '@cropper/elements'
import { type ShallowRef } from 'vue'

export const useImgCropper = (
  refCropperCanvas: Readonly<ShallowRef<CropperCanvas | null>>,
  refCropperImage: Readonly<ShallowRef<CropperImage | null>>,
) => {
  const scaleX = 1
  const scaleY = 1

  const elCanvas = () => refCropperCanvas.value as CropperCanvas
  const elImage = () => refCropperImage.value as CropperImage

  const rotate = (degree: number) => {
    elImage().$rotate(`${degree}deg`)
  }
  const flipHorizontal = () => {
    elImage().$scale(-1, scaleY)
  }
  const flipVertical = () => {
    elImage().$scale(scaleX, -1)
  }
  const alignHorizontalLeft = () => {
    const targetX = elCanvas().getBoundingClientRect().x
    const currentX = elImage().getBoundingClientRect().x
    elImage().$move(targetX - currentX, 0)
  }
  const alignHorizontalCenter = () => {
    const targetX = elCanvas().getBoundingClientRect().x + elCanvas().getBoundingClientRect().width / 2
    const currentX = elImage().getBoundingClientRect().x + elImage().getBoundingClientRect().width / 2
    elImage().$move(targetX - currentX, 0)
  }
  const alignHorizontalRight = () => {
    const targetX = elCanvas().getBoundingClientRect().x + elCanvas().getBoundingClientRect().width
    const currentX = elImage().getBoundingClientRect().x + elImage().getBoundingClientRect().width
    elImage().$move(targetX - currentX, 0)
  }
  const alignVerticalTop = () => {
    const targetY = elCanvas().getBoundingClientRect().y
    const currentY = elImage().getBoundingClientRect().y
    elImage().$move(0, targetY - currentY)
  }
  const alignVerticalMiddle = () => {
    const targetY = elCanvas().getBoundingClientRect().y + elCanvas().getBoundingClientRect().height / 2
    const currentY = elImage().getBoundingClientRect().y + elImage().getBoundingClientRect().height / 2
    elImage().$move(0, targetY - currentY)
  }
  const alignVerticalBottom = () => {
    const targetY = elCanvas().getBoundingClientRect().y + elCanvas().getBoundingClientRect().height
    const currentY = elImage().getBoundingClientRect().y + elImage().getBoundingClientRect().height
    elImage().$move(0, targetY - currentY)
  }
  const fitToHeight = () => {
    const { width, height } = elImage().getBoundingClientRect()
    if (height > width) {
      autoFitContain()
    } else {
      autoFitCover()
    }
  }
  const fitToWidth = () => {
    const { width, height } = elImage().getBoundingClientRect()
    if (width > height) {
      autoFitContain()
    } else {
      autoFitCover()
    }
  }
  const autoFitCover = () => {
    elImage().$center('cover')
  }
  const autoFitContain = () => {
    elImage().$center('contain')
  }

  const getResultBlob = async () => {
    // Get the native image element from CropperImage's shadowRoot
    const imgEl = elImage().shadowRoot?.querySelector('img') as HTMLImageElement | null
    const naturalWidth = imgEl?.naturalWidth || 0
    const naturalHeight = imgEl?.naturalHeight || 0
    // Export cropped area at original resolution if possible
    const newCanvas = (naturalWidth && naturalHeight)
      ? await elCanvas().$toCanvas({ width: naturalWidth, height: naturalHeight })
      : await elCanvas().$toCanvas()
    return new Promise<Blob | null>((resolve) => {
      newCanvas.toBlob((blob) => {
        resolve(blob)
      }, 'image/png')
    })
  }

  return {
    elCanvas,
    elImage,
    rotate,
    flipHorizontal,
    flipVertical,
    alignHorizontalLeft,
    alignHorizontalCenter,
    alignHorizontalRight,
    alignVerticalTop,
    alignVerticalMiddle,
    alignVerticalBottom,
    fitToHeight,
    fitToWidth,
    autoFitCover,
    autoFitContain,
    getResultBlob,
  }
}
